import { Fragment, type ReactNode } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '@/shared/utils/cn';

export interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  position?: 'left' | 'right';
  title?: string;
  children: ReactNode;
  className?: string;
  showCloseButton?: boolean;
}

export function MobileDrawer({
  isOpen,
  onClose,
  position = 'left',
  title,
  children,
  className,
  showCloseButton = true,
}: MobileDrawerProps) {
  const isLeft = position === 'left';

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-80 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div
              className={cn(
                'pointer-events-none fixed inset-y-0 flex max-w-full',
                isLeft ? 'left-0 pr-10' : 'right-0 pl-10'
              )}
            >
              <TransitionChild
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom={isLeft ? '-translate-x-full' : 'translate-x-full'}
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo={isLeft ? '-translate-x-full' : 'translate-x-full'}
              >
                <DialogPanel
                  className={cn(
                    'pointer-events-auto w-screen max-w-sm',
                    className
                  )}
                >
                  <div className="flex h-full flex-col bg-white dark:bg-gray-800 shadow-xl">
                    {(title || showCloseButton) && (
                      <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-4 py-4">
                        {title && (
                          <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {title}
                          </DialogTitle>
                        )}
                        {!title && <div />}
                        {showCloseButton && (
                          <button
                            type="button"
                            className="rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px] min-w-[44px] flex items-center justify-center"
                            onClick={onClose}
                          >
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    )}

                    <div className="flex-1 overflow-y-auto">
                      {children}
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
